import React, {useState, useCallback, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody';
import SMCButton from '../../components/Simple/SMCButton';
import SMCModal from '../../components/core/SMCModal';
import {ObjectTypes} from '../../api/consts';
import Api from '../../api/SMPFetchHelper';
import SMCTable from '../../components/core/SMCTable/SMCTable';
import {AdministratorGetListSchema, AdministratorStatuses, AdministratorRoles} from '../../api/DBSchema/Administrator';
import DescriptionCell from '../../components/core/SMCTable/CommonCells/DescriptionCell';
import connectedLogo from '../../assets/images/gateway/connected.png';
import disconnectedLogo from '../../assets/images/gateway/disconnected.png';
import SMCLabel from '../../components/Simple/SMCLabel';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useSMCErrorHandler} from '../../utils/errorUtils';

const RoleCell = React.memo(({value}) => {
    const { t } = useTranslation();

    switch (value) {
        case AdministratorRoles.administrator:
            return <span>{t('SMC_ADMIN_ROLE_Administrator')}</span>;
        case AdministratorRoles.readOnly:
            return <span>{t('SMC_ADMIN_ROLE_ReadOnly')}</span>;
        case AdministratorRoles.multiDomain:
            return <span>{t('SMC_ADMIN_ROLE_MultiDomain')}</span>;
        case AdministratorRoles.multiDomainReadOnly:
            return <span>{t('SMC_ADMIN_ROLE_MultiDomainReadOnly')}</span>;
        default:
            return undefined;
    }
});

const StatusCell = React.memo(({value}) => {
    const { t } = useTranslation();

    if (value === AdministratorStatuses.connected) {
        return <img src={connectedLogo} title={t('SMC_USER_LOGGED_IN')} alt="logged in" />;
    } else {
        return <img src={disconnectedLogo} title={t('SMC_USER_NOT_LOGGED_IN')} alt="not logged in" />;
    }
});

function SetAdminModal({
    isOpen,
    disableSelectById,
    onRequestClose,
    onSuccess,
    selectMultiple
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();

    const adminTableColumns = useMemo(() => ([
        { id: 'userId',      accessor: AdministratorGetListSchema.userId,      header: t('SMP.ADMINISTRATOR.USER_ID'),    width: 130,                                        },
        { id: 'description', accessor: AdministratorGetListSchema.description, header: '',                                width: 32,                   Cell: DescriptionCell },
        { id: 'firstName',   accessor: AdministratorGetListSchema.firstName,   header: t('SMP.ADMINISTRATOR.FIRST_NAME'), width: 115                                         },
        { id: 'lastName',    accessor: AdministratorGetListSchema.lastName,    header: t('SMP.ADMINISTRATOR.LAST_NAME'),  width: 115                                         },
        { id: 'email',       accessor: AdministratorGetListSchema.email,       header: t('SMP.ADMINISTRATOR.EMAIL'),      width: 215                                         },
        { id: 'phone',       accessor: AdministratorGetListSchema.phone,       header: t('SMP.ADMINISTRATOR.PHONE'),      width: 115                                         },
        { id: 'role',        accessor: AdministratorGetListSchema.role,        header: t('SMP.ADMINISTRATOR.ROLE'),       width: 115,                  Cell: RoleCell        },
        { id: 'status',      accessor: AdministratorGetListSchema.status,      header: t('SMP.ADMINISTRATOR.STATUS'),     width: 55,  align: 'center', Cell: StatusCell      }
    ]), [t]);

    const [selectedObjects, setSelectedObjects] = useState([]);
    const [isTableDataLoading, setIsTableDataLoading] = useState(false);
    const [adminTableData, setAdminTableData] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleTableSelect = useCallback((selectedObjects) => {
        let newSelectedObjects = [...selectedObjects];
        if (!selectMultiple) {
            // If selected 2, and only alowed one, replace the originally selected with the new one
            if (newSelectedObjects.length === 2) {
                newSelectedObjects = [newSelectedObjects[1]]
            }
            // Not alowed multiselect
            else if (newSelectedObjects.length > 2) {
                return;
            }
        }
        setSelectedObjects(newSelectedObjects);
    }, [selectMultiple]);

    const loadData = useCallback(() => {
        setIsTableDataLoading(true);
        Api.fetchObjectList(ObjectTypes.ADMINISTRATOR)
        .then((resp) => {
            setAdminTableData(resp.data.data);
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            setErrorMessage(errorMessage);
        })
        .finally(() => {
            setIsTableDataLoading(false);
        });
    }, [getErrorMessage]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (isOpen) {
            setSelectedObjects([]);
        }
    }, [isOpen]);

    const handleSetAdmin = useCallback(() => {
        if (selectedObjects.length > 0) {
            onSuccess(selectedObjects);
        }
    }, [selectedObjects, onSuccess]);

    return (
        <SMCModal
            title="Set administrator"
            height={620}
            width={690}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            errorMessage={errorMessage}
            reloadData={loadData}
        >
            <SMCModalBody>
                <SMCLabel bold>{t('SMC_USERWIZ_CHOOSE')}</SMCLabel>
                <SMCTable
                    loading={isTableDataLoading}
                    content={adminTableData}
                    columns={adminTableColumns}
                    width={630}
                    height={350}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={handleTableSelect}
                    rowIdSelector={obj => obj[AdministratorGetListSchema.userId]}
                    isRowSelectable={obj => Array.isArray(disableSelectById) ? !disableSelectById.includes(obj[AdministratorGetListSchema.userId]) : true}
                    hasCheckAll={true}
                />
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={handleSetAdmin} disabled={selectedObjects.length === 0}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    Set administrator
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>
    );
}

export default SetAdminModal;